import React, { useEffect, useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { ArrowSmRightIcon } from '@heroicons/react/solid';
import classNames from 'classnames';

// Shuffle function to randomize array order (Fisher-Yates algorithm)
const shuffleArray = (array) => {
  // Create a copy of the array to avoid mutating the original
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    // Pick a random index from 0 to i
    const j = Math.floor(Math.random() * (i + 1));
    // Swap elements at i and j
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const BeneficiaryCarousel = ({ className }) => {
  const { landlordCards } = useStaticQuery(graphql`
      {
        landlordCards: contentfulLandlordCard(name: {eq: "Home Page Landlord"}) {
          id
          name
          landlord {
            id
            company
            logo {
              title
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 200                   
                    layout: CONSTRAINED
                    placeholder: BLURRED
                    formats: [WEBP]
                  )
                }
              }
            }
          }
        }
      }
  `);

  // Create refs to access the DOM elements
  const trackRef = useRef(null);
  const containerRef = useRef(null);

  // Flatten the items from all Landlord Cards to get the logo items
  const logoItems = landlordCards?.landlord;

  if (!logoItems?.length) return null;

  // Shuffle the logo items to randomize their order
  const shuffledLogoItems = shuffleArray(logoItems);

  // Create a duplicate array of items to create the continuous loop effect
  const logoItemsForDisplay = [...shuffledLogoItems, ...shuffledLogoItems];

  // Effect to calculate and set the track width as a CSS variable
  useEffect(() => {
    const calculateTrackWidth = () => {
      if (trackRef.current && containerRef.current) {
        const trackWidth = trackRef.current.scrollWidth;
        // Set the track width as a CSS variable on the container
        containerRef.current.style.setProperty('--track-width', `${trackWidth / 2}`);
      }
    };

    // Calculate initially
    calculateTrackWidth();

    // Recalculate on window resize
    window.addEventListener('resize', calculateTrackWidth);

    // Cleanup
    return () => {
      window.removeEventListener('resize', calculateTrackWidth);
    };
  }, [logoItems]); // Recalculate when the items change

  return (
    <div className={classNames('w-full my-10', className)}>
      <div className="relative">
        <h2 className='w-full space-x-2 text-center text-4xl tracking-tighter md:text-5xl pb-10'>
          <span>Our growing beneficiary network</span>
          <div className='relative -top-2 inline-block'>
            <ArrowSmRightIcon className='h-6 xl:h-8' />
          </div>
        </h2>

        {/* Container with max width to limit visible items */}
        <div className="max-w-4xl mx-auto">
          <div ref={containerRef} className="beneficiary-carousel-container p-2">
            <div ref={trackRef} className="beneficiary-carousel-track">
              {logoItemsForDisplay.map((item, index) => {
                // Try to get the image data through multiple potential paths
                const gatsbyImageData =
                  item.logo?.gatsbyImageData ||
                  item.logo?.localFile?.childImageSharp?.gatsbyImageData;

                if (!gatsbyImageData) {
                  return null; // Skip this item if no image data
                }

                return (
                  <div
                    key={`${item.id}-${index}`}
                    className="beneficiary-item flex-shrink-0 relative bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                  >
                    {/* Image positioned as background */}
                    <div className="absolute inset-0 z-0 flex items-center justify-center">
                      <GatsbyImage
                        loading="eager"
                        image={gatsbyImageData}
                        alt={item.company || "Partner logo"}
                        className="w-auto h-auto object-contain"
                      />
                    </div>

                    {/* Box overlay to ensure the card styling is always visible */}
                    <div className="relative z-10 w-full h-full p-4 flex items-center justify-center bg-transparent">
                      {/* This is an empty div that maintains the box structure */}
                    </div>
                  </div>
                );
              }).filter(Boolean)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeneficiaryCarousel;
